import { TFunction } from "i18next";
import * as yup from "yup";
import { ParaiskaFileValues } from "../../resources/paraiskos";
import { ArraySchema } from "yup";

export function setValidationSchemaLocale(t: TFunction): void {
    yup.setLocale({
        string: {
            length: (params) => t("validation.length", { length: params.length }),
            email: t("validation.email"),
        },
        mixed: {
            required: t("validation.required"),
        },
        number: {
            min: (params) => t("validation.min", { min: params.min }),
            positive: t("validation.positive"),
        },
        array: {
            min: (params) => t("validation.arrayMin", { min: params.min }),
        },
    });
}

export function yupPersonCode(t: TFunction) {
    return yup
        .string()
        .matches(/^\d{11}$/, t("validation.personCode"))
        .matches(/^[3-6]\d{10}$/, t("validation.personCodeFormat"));
}

export function yupPersonCodeOrDate(t: TFunction) {
    return yup.string().test("valid-personCodeOrDate", t("validation.invalidPersonCodeOrDate"), (value: string) => {
        if (value && value.match(/^\d{11}$/) && value.match(/^[3-6]\d{10}$/)) {
            return validPersonCode(value);
        } else if (value && value.match(/^\d{4}[\-](0?[1-9]|1[012])[\-](0?[1-9]|[12][0-9]|3[01])$/)) {
            const dateParse = value.split("-");
            const date = new Date(Number(dateParse[0]), Number(dateParse[1]) - 1, Number(dateParse[2]));
            if (Number(dateParse[2]) == date.getDate()) {
                return true;
            }
        }
        return false;
    });
}

export function yupPhone(t: TFunction) {
    return yup.string().matches(/^\+370\d{8}$/, t("validation.phone"));
}

// Telefono validacija (skyrikliai: tarpai, brūkšneliai, skliaustai – ignoruojami):
//   • tarptautinis E.164: +<šalies kodas><nr>, +371…+ iki 15 skaitmenų (pvz. +37061234567)
//   • lietuviškas nacionalinis su 0 pradžioje: 0######## (pvz. 061234567)
// Pastaba: 8 priekyje LT nebenaudojamas, todėl nebepriimamas.
// Tuščią reikšmę praleidžia (skirta atskiram .required() tikrinimui).
export function yupPhoneE164(t: TFunction) {
    return yup.string().test(
        "phone-e164",
        t("validation.phoneE164"),
        (value?: string | null) => {
            if (!value) return true;
            const cleaned = value.replace(/[\s()\-]/g, "");
            return /^(?:\+[1-9]\d{1,14}|0\d{8})$/.test(cleaned);
        }
    );
}

export function yupName(msg: string) {
    return yup.string().matches(/[A-ZĄ-Ž][A-Za-zĄ-Žą-ž ,.'-]{1,}(?:[A-ZĄ-Ž][A-Za-zĄ-Žą-ž ,.'-]*){0,}$/, msg);
}

export function yupPostcode(t: TFunction) {
    return yup.string().matches(/^\d{5}$/, t("validation.postcode"));
}

export function yupEmail(t: TFunction) {
    // yup .email() validates the local part and general structure, but its domain part is too
    // permissive: it allows underscores and hyphenated/numeric TLDs (e.g. "x@pep__a.lt", "x@y.l-t").
    // The extra matches() enforces proper hostname rules on the domain after "@":
    //   - each label: alphanumerics + inner hyphens only (no underscore, no leading/trailing hyphen)
    //   - the TLD: letters only, 2-63 chars
    // Empty is allowed (both checks skip empty) — chain .required() where needed.
    return yup
        .string()
        .email(t("validation.email"))
        .matches(/@([A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,63}$/, {
            message: t("validation.email"),
            excludeEmptyString: true,
        });
}

export function yupUrl(t: TFunction) {
    // REQUIRED http(s) scheme + domain + alphabetic TLD (2-63 letters) + optional port/path;
    // empty allowed (matches() ignores empty). TLD must be letters only, so "lt9999" is invalid.
    // Scheme is mandatory: a bare domain like "lalala.lt" is rejected.
    return yup.string().matches(
        /^(https?:\/\/)([\w-]+\.)+[A-Za-z]{2,63}(:\d+)?(\/[^\s]*)?$/i,
        t("validation.url")
    );
}

export function yupFiles(pavadinimasRequired: boolean = true): ArraySchema<ParaiskaFileValues> {
    return yup.array<ParaiskaFileValues>(
        yup.object<ParaiskaFileValues>({
            id: yup.string().notRequired(),
            tipas: yup.string().required(),
            pavadinimas: pavadinimasRequired ? yup.string().required() : yup.string().notRequired(),
            file: yup.mixed<File>().when("fileUrl", (fileId: string, schema: any) => {
                return fileId ? schema.notRequired() : schema.required();
            }),
        })
    );
}

export function yupL03Files(t: TFunction): ArraySchema<ParaiskaFileValues> {
    return yup.array<ParaiskaFileValues>(
        yup
            .object<ParaiskaFileValues>({
                tipas: yup.string().required(),
                pavadinimas: yup.string().required(),
                file: yup.mixed<File>().when("fileUrl", (fileId: string, schema: any) => {
                    return fileId ? schema.notRequired() : schema.required();
                }),
            })
            .test({
                name: "NZT-type-validator",
                test: function (item) {
                    const nztVal = 53;
                    //const moksTyrAtaskVal = 54;

                    let typeFound = false;

                    let array = this.parent;
                    let arraySize = array.length;

                    if (!typeFound) {
                        for (let index = 0; index < arraySize; index++) {
                            let element = array[index];
                            let docTipas = element.tipas;

                            //let hasNext = index + 1 < arraySize ? true : false;

                            if (docTipas != undefined && docTipas == nztVal) {
                                typeFound = true;
                                break;
                            }
                        }
                    }

                    return typeFound ? typeFound : this.createError({ path: `${this.path}.tipas`, message: t("validation.nztFileRequired") });
                },
            })
    );
}

export function validPersonCode(code?: string) {
    if (code && code.match(/\d{11}/)) {
        let b = 1,
            c = 3,
            d = 0,
            e = 0;
        for (let i = 0; i < 10; i++) {
            const a = Number(code[i]);
            d += b * a;
            e += c * a;
            if (++b == 10) {
                b = 1;
            }
            if (++c == 10) {
                c = 1;
            }
        }
        d = d % 11;
        e = e % 11;
        let r = d == 10 ? (e == 10 ? 0 : e) : d;
        return Number(code[10]) == r;
    }
    return false;
}
